import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  EncryptedEnvelopeV1,
  PAYLOAD_ENCRYPTION,
} from '../constants/payload-encryption.constants';

/**
 * AES-256-GCM using Web Crypto API (browser).
 * Key: 32 bytes from base64 in environment.
 */
@Injectable({ providedIn: 'root' })
export class PayloadCryptoService {
  private keyPromise: Promise<CryptoKey> | null = null;

  /**
   * True only when env key is set AND Web Crypto subtle is available.
   * `crypto.subtle` is missing on non-secure origins (e.g. http://192.168.x.x) — use HTTPS or localhost for encryption.
   */
  isConfigured(): boolean {
    if (!this.hasSubtleCrypto()) {
      return false;
    }
    return (
      !!environment.payloadEncryptionEnabled &&
      typeof environment.payloadEncryptionKeyBase64 === 'string' &&
      environment.payloadEncryptionKeyBase64.length > 0
    );
  }

  private hasSubtleCrypto(): boolean {
    return (
      typeof globalThis !== 'undefined' &&
      typeof globalThis.crypto !== 'undefined' &&
      typeof globalThis.crypto.subtle !== 'undefined'
    );
  }

  async encryptUtf8(plainText: string): Promise<EncryptedEnvelopeV1> {
    const key = await this.getKey();
    const iv = crypto.getRandomValues(new Uint8Array(PAYLOAD_ENCRYPTION.IV_LENGTH));
    const encoded = new TextEncoder().encode(plainText);
    const cipherBuf = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv as BufferSource,
        tagLength: PAYLOAD_ENCRYPTION.TAG_LENGTH,
      },
      key,
      encoded,
    );
    return {
      v: PAYLOAD_ENCRYPTION.VERSION,
      iv: this.bytesToBase64(iv),
      data: this.bytesToBase64(new Uint8Array(cipherBuf)),
    };
  }

  async decryptToUtf8(envelope: EncryptedEnvelopeV1): Promise<string> {
    if (envelope.v !== PAYLOAD_ENCRYPTION.VERSION) {
      throw new Error(`Unsupported encryption version: ${envelope.v}`);
    }
    const key = await this.getKey();
    const iv = this.base64ToBytes(envelope.iv);
    const data = this.base64ToBytes(envelope.data);
    const plainBuf = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv as BufferSource,
        tagLength: PAYLOAD_ENCRYPTION.TAG_LENGTH,
      },
      key,
      data as BufferSource,
    );
    return new TextDecoder().decode(plainBuf);
  }

  private async getKey(): Promise<CryptoKey> {
    if (!this.isConfigured()) {
      throw new Error('Payload encryption key not configured');
    }
    if (!this.keyPromise) {
      const raw = this.decodeKeyMaterial(environment.payloadEncryptionKeyBase64!);
      this.keyPromise = crypto.subtle.importKey('raw', raw as BufferSource, 'AES-GCM', false, [
        'encrypt',
        'decrypt',
      ]);
    }
    return this.keyPromise;
  }

  private decodeKeyMaterial(b64: string): Uint8Array {
    const bytes = this.base64ToBytes(b64);
    if (bytes.length !== 32) {
      throw new Error(
        `AES-256 key must be 32 bytes after base64 decode; got ${bytes.length}`,
      );
    }
    return bytes;
  }

  private base64ToBytes(b64: string): Uint8Array {
    const binary = atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      out[i] = binary.charCodeAt(i);
    }
    return out;
  }

  private bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}
